import { useNavigate, useParams } from 'react-router-dom';
import useCallApiAndLoad from '../../hooks/useCallApiAndLoad';
import useAuth from '../../hooks/useAuth';
import { useCallback, useState } from 'react';
import {
  deletePcvMediaItemAllEndpoint,
  deletePcvMediaItemEndpoint,
  getUnitPcvDetailEndpoint,
  sortPcvMediaItemEndpoint,
  uploadPcvResourcesEndpoint,
  uploadPcvViewResourcesEndpoint,
} from './services/unitPcvService';
import useAdminEntity from '../../hooks/useAdminEntity';
import { UnitPcvItemList, UnitViewGallery } from './models/unitPcv';
import LoadingCard from '../../components/LoadingCard';
import AdminPage from '../../components/admin/AdminPage';
import { Col, Row } from 'reactstrap';
import AdminUploadResource from '../../components/admin/AdminUploadResource';
import AdminResourceGallery from '../../components/admin/AdminResourceGallery';
import AdminUploadViewResource from '../../components/admin/AdminUploadViewResource';
import AdminViewGallery from '../../components/admin/AdminViewGallery';

const UnitPcvUploadMedia = () => {
  const { pcvId = '' } = useParams();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const navigate = useNavigate();

  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  const [errorAlert, setErrorAlert] = useState<any>(null);

  const getPcvItem = useCallback(
    () => getUnitPcvDetailEndpoint(pcvId, token),
    [pcvId, token],
  );
  const [pcvItem] = useAdminEntity<UnitPcvItemList>(getPcvItem, setErrorAlert);

  const pageName = `Recursos de la pcv`;
  const pageParentName = `PCV`;
  const globalAlertOptions = {
    success: false,
    warning: true,
    onConfirm: () => setErrorAlert(null),
    title: 'Error',
    text: errorAlert || '',
  };

  const handleBlueprint = useCallback(
    async (files: Array<any>) => {
      if (files.length === 0) return;

      const formData = new FormData();
      files.forEach((file) => formData.append('blueprint', file));
      const { status } = await callEndpoint(
        uploadPcvResourcesEndpoint(pcvId, formData, token),
      );

      if (status === 200) navigate(0);
    },
    [pcvId, callEndpoint, navigate, token],
  );

  const handleGallery = useCallback(
    async (files: Array<any>) => {
      if (files.length === 0) return;
      const formData = new FormData();
      files.forEach((file) => formData.append('gallery', file));
      const { status } = await callEndpoint(
        uploadPcvResourcesEndpoint(pcvId, formData, token),
      );

      if (status === 200) navigate(0);
    },
    [callEndpoint, navigate, token],
  );

  const handleViewGallery = useCallback(
    async (entries: Array<{ view: string; file: File }>) => {
      if (entries.length === 0) return;
      const formData = new FormData();
      entries.forEach(({ view, file }) => formData.append(view, file));
      const { status } = await callEndpoint(
        uploadPcvViewResourcesEndpoint(pcvId, formData, token),
      );

      if (status === 200) navigate(0);
    },
    [pcvId, callEndpoint, navigate, token],
  );

  const handleSortGallery = useCallback(
    async (items: string[]) => {
      await callEndpoint(
        sortPcvMediaItemEndpoint(pcvId, { gallery: items }, token),
      );
    },
    [pcvId, callEndpoint, token],
  );

  const handleSortViewGallery = useCallback(
    async (items: UnitViewGallery[]) => {
      await callEndpoint(
        sortPcvMediaItemEndpoint(pcvId, { mediaView: items }, token),
      );
    },
    [pcvId, callEndpoint, token],
  );

  const handleDeleteGallery = useCallback(
    async (image: string) => {
      const { status } = await callEndpoint(
        deletePcvMediaItemEndpoint(pcvId, { gallery: image }, token),
      );
      if (status === 200) navigate(0);
    },
    [pcvId, callEndpoint, navigate, token],
  );

  const handleDeleteAllGallery = useCallback(async () => {
    const { status } = await callEndpoint(
      deletePcvMediaItemAllEndpoint(pcvId, { gallery: true }, token),
    );
    if (status === 200) navigate(0);
  }, [pcvId, callEndpoint, navigate, token]);

  const handleDeleteViewGallery = useCallback(
    async (item: UnitViewGallery) => {
      if (item.image) {
        const { status } = await callEndpoint(
          deletePcvMediaItemEndpoint(pcvId, { mediaView: item.image }, token),
        );
        if (status === 200) navigate(0);
      }
    },
    [pcvId, callEndpoint, navigate, token],
  );

  const handleDeleteAllViewGallery = useCallback(async () => {
    const { status } = await callEndpoint(
      deletePcvMediaItemAllEndpoint(pcvId, { mediaView: true }, token),
    );
    if (status === 200) navigate(0);
  }, [pcvId, callEndpoint, navigate, token]);

  if (!pcvItem)
    return <LoadingCard name={pageName} parent_name={pageParentName} />;

  const coverGallery = [pcvItem.blueprint].filter((img) => img !== null);

  const modelGallery = [...pcvItem.gallery].filter((img) => img !== null);
  return (
    <AdminPage
      name={pageName}
      parentName={pageParentName}
      globalAlertOptions={globalAlertOptions}
    >
      <Row>
        <Col>
          <AdminUploadResource
            handleUpload={handleBlueprint}
            description={'Subir cover'}
            maxFiles={1}
          />
        </Col>
        <Col>
          <AdminResourceGallery
            gallery={coverGallery}
            description={'Cover actual'}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <AdminUploadResource
            handleUpload={handleGallery}
            description={'Subir galería'}
          />
        </Col>
        <Col>
          <AdminResourceGallery
            gallery={modelGallery}
            description={'Galería actual'}
            onDrag={handleSortGallery}
            onDelete={handleDeleteGallery}
            onDeleteAll={handleDeleteAllGallery}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <AdminUploadViewResource
            description={'Subir vistas'}
            handleUpload={handleViewGallery}
          />
        </Col>
        <Col>
          <AdminViewGallery
            gallery={pcvItem.view_gallery}
            description={'Vistas actuales'}
            onDrag={handleSortViewGallery}
            onDelete={handleDeleteViewGallery}
            onDeleteAll={handleDeleteAllViewGallery}
          />
        </Col>
      </Row>
    </AdminPage>
  );
};

export default UnitPcvUploadMedia;
